import React, { useEffect, useRef } from 'react';
import { Message } from '../../types';
import MessageItem from './MessageItem';

interface MessageListProps {
  messages: Message[];
  currentUserId: string;
  hasMore: boolean;
  loadingMore: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onLoadMore: () => void;
  onImageClick: (imageUrl: string) => void;
  onAddReaction: (messageId: number, emoji: string) => void;
  onRemoveReaction: (reactionId: number) => void;
  onDeleteMessage: (messageId: number) => void;
  onReplyToMessage?: (message: Message) => void;
  otherUserName?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  hasMore,
  loadingMore,
  messagesEndRef,
  onLoadMore,
  onImageClick,
  onAddReaction,
  onRemoveReaction,
  onDeleteMessage,
  onReplyToMessage,
  otherUserName
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (loadingRef.current || !hasMore || loadingMore) return;

      const scrollTop = container.scrollTop;
      
      if (scrollTop < 100) {
        loadingRef.current = true;
        const previousScrollHeight = container.scrollHeight;
        
        onLoadMore();
        
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = scrollTop + (newScrollHeight - previousScrollHeight);
          }
          loadingRef.current = false;
        }, 300);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto p-4 space-y-3">
      {hasMore && messages.length > 0 && loadingMore && (
        <div className="w-full py-2 text-center text-[#06B6D4] text-sm">
          Loading more messages...
        </div>
      )}
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full text-[#A1A1AA]">
          No messages yet. Start a conversation!
        </div>
      ) : (
        messages.map((msg) => (
          <MessageItem
            key={msg.id}
            message={msg}
            currentUserId={currentUserId}
            onImageClick={onImageClick}
            onAddReaction={onAddReaction}
            onRemoveReaction={onRemoveReaction}
            onDeleteMessage={onDeleteMessage}
            onReplyToMessage={onReplyToMessage}
            otherUserName={otherUserName}
          />
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
